import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { leaveAPI } from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { notify, reportLoadError } from '../../utils/notify';
import { tn } from '../../utils/plural';
import { TriangleAlert } from 'lucide-react';
import Modal from '../../components/Modal';
import StatusBadge from '../../components/StatusBadge';
import { announceLeaveChanged } from './pendingLeave';
import styles from './TimeAttendance.module.css';

// The roles the backend lets approve or reject leave (LEAVE_DECIDERS in
// leaveRoutes.js).
const DECIDING_ROLES = ['super_admin', 'hr_manager', 'operations_manager', 'finance_manager'];
const STATUSES = [['', 'All'], ['pending', 'Pending'], ['approved', 'Approved'], ['rejected', 'Rejected']];

const fullName = (r) => `${r.first_name || ''} ${r.last_name || ''}`.trim();

/**
 * Time & Attendance > Leave: every leave request, pending first, with the
 * employee's leave days used this year by type, overlaps with other
 * approved leave or days already recorded, and approve / reject on the same
 * line (our own dialog). Approving here is the same as on the Approvals page.
 */
const LeaveTab = () => {
  const { t, locale } = useLanguage();
  const tRef = useRef(t);
  tRef.current = t;
  const { user } = useContext(AuthContext);
  const canDecide = Boolean(user && DECIDING_ROLES.includes(user.role));

  const [filters, setFilters] = useState({ status: '', employee_id: '', leave_type_id: '' });
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null); // { request, approve }
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const filterKey = JSON.stringify(filters);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(JSON.parse(filterKey)).filter(([, v]) => v));
      const res = await leaveAPI.getBoard(params);
      setBoard(res.data);
    } catch (error) {
      reportLoadError(error, tRef.current('Could not load the leave requests'), { main: true });
    } finally {
      setLoading(false);
    }
  }, [filterKey]);

  useEffect(() => { load(); }, [load]);

  const date = (iso, withYear) => new Date(`${iso}T00:00:00`).toLocaleDateString(locale, withYear
    ? { day: 'numeric', month: 'short', year: 'numeric' } : { day: 'numeric', month: 'short' });
  const range = (r) => (r.start_date === r.end_date ? date(r.start_date, true) : `${date(r.start_date)} – ${date(r.end_date, true)}`);

  const open = (request, approve) => {
    setNote('');
    setDecision({ request, approve });
  };

  const decide = async () => {
    const { request, approve } = decision;
    setBusy(true);
    try {
      if (approve) await leaveAPI.approve(request.id, { notes: note.trim() || undefined });
      else await leaveAPI.reject(request.id, { reason: note.trim() });
      notify(approve ? t('Leave approved') : t('Leave rejected'), 'success');
      announceLeaveChanged(); // the menu's counter
      setDecision(null);
      await load();
    } catch (error) {
      notify(error.response?.data?.message || error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const warnings = (r) => {
    const list = [];
    r.overlapping_requests.forEach((o) => list.push(t('Overlaps approved leave: {type}, {range}', {
      type: o.leave_type_name, range: range(o),
    })));
    if (r.recorded_days > 0) {
      list.push(tn(t, r.recorded_days,
        '{count} day of this range already has another record (approving turns it into leave)',
        '{count} days of this range already have another record (approving turns them into leave)'));
    }
    return list;
  };

  if (!board) {
    return (
      <div className={styles.empty}>
        {loading ? t('Loading...') : <button type="button" className="btn btn--secondary" onClick={load}>{t('Retry')}</button>}
      </div>
    );
  }

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  return (
    <div>
      <div className={styles.weekToolbar}>
        <div className={styles.weekFilters}>
          <label className={styles.field}>
            <span>{t('Status')}</span>
            <select className="select" value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
              {STATUSES.map(([value, label]) => <option key={value} value={value}>{t(label)}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span>{t('Employee')}</span>
            <select className="select" value={filters.employee_id} onChange={(e) => setFilter('employee_id', e.target.value)}>
              <option value="">{t('All employees')}</option>
              {board.employees.map((e) => <option key={e.id} value={e.id}>{fullName(e)}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span>{t('Leave type')}</span>
            <select className="select" value={filters.leave_type_id} onChange={(e) => setFilter('leave_type_id', e.target.value)}>
              <option value="">{t('All types')}</option>
              {board.leave_types.map((lt) => <option key={lt.id} value={lt.id}>{lt.name}</option>)}
            </select>
          </label>
        </div>
        <span className={styles.pendingCount} aria-live="polite">
          {tn(t, board.pending, '{count} request waiting for a decision', '{count} requests waiting for a decision')}
        </span>
      </div>

      <ul className={styles.leaveList} aria-busy={loading}>
        {board.requests.map((r) => {
          const used = board.used_by_employee[r.employee_id] || [];
          const notes = warnings(r);
          return (
            <li key={r.id} className={`${styles.leaveItem} ${r.status === 'pending' ? styles.leavePending : ''}`} data-status={r.status}>
              <div className={styles.leaveMain}>
                <div className={styles.leaveWho}>
                  <span className={styles.name}>{fullName(r)}</span>
                  <span className={styles.muted}>
                    {used.length > 0
                      ? t('Used in {year}: {list}', { year: board.year, list: used.map((u) => `${u.name} ${tn(t, u.days, '{count} day', '{count} days')}`).join(' · ') })
                      : t('No leave used in {year}', { year: board.year })}
                  </span>
                </div>
                <div className={styles.leaveWhat}>
                  <span className={styles.leaveType}>
                    <span className={styles.leaveDot} style={{ background: r.leave_type_color || '#a16207' }} aria-hidden="true" />
                    {r.leave_type_name}
                    <span className={styles.muted}> · {r.leave_is_paid ? t('paid') : t('unpaid')}</span>
                  </span>
                  <span>{range(r)} · {tn(t, r.weekdays, '{count} weekday', '{count} weekdays')}</span>
                  {r.reason && <span className={styles.muted}>“{r.reason}”</span>}
                </div>
                <div className={styles.leaveState}>
                  <StatusBadge status={r.status}>{t(r.status === 'pending' ? 'Pending' : r.status === 'approved' ? 'Approved' : 'Rejected')}</StatusBadge>
                  {r.status === 'rejected' && r.rejection_reason && <span className={styles.muted}>{r.rejection_reason}</span>}
                  {r.status !== 'pending' && r.decided_by && <span className={styles.muted}>{t('by {who}', { who: r.decided_by })}</span>}
                </div>
                {r.status === 'pending' && canDecide && (
                  <div className={styles.leaveActions}>
                    <button type="button" className="btn btn--primary" onClick={() => open(r, true)}>{t('Approve')}</button>
                    <button type="button" className="btn btn--secondary" onClick={() => open(r, false)}>{t('Reject')}</button>
                  </div>
                )}
              </div>
              {r.status === 'pending' && notes.length > 0 && (
                <ul className={styles.leaveWarnings}>
                  {notes.map((n) => <li key={n}><TriangleAlert aria-hidden="true" /> {n}</li>)}
                </ul>
              )}
            </li>
          );
        })}
        {board.requests.length === 0 && <li className={styles.empty}>{t('No leave requests match')}</li>}
      </ul>

      <Modal
        open={Boolean(decision)}
        title={decision && decision.approve ? t('Approve leave') : t('Reject leave')}
        onClose={() => setDecision(null)}
        busy={busy}
        size="sm"
        footer={decision && (
          <>
            <button type="button" className="btn btn--secondary" onClick={() => setDecision(null)} disabled={busy}>{t('Cancel')}</button>
            <button
              type="button"
              className={decision.approve ? 'btn btn--primary' : 'btn btn--danger'}
              onClick={decide}
              disabled={busy || (!decision.approve && !note.trim())}
            >
              {decision.approve ? t('Approve') : t('Reject')}
            </button>
          </>
        )}
      >
        {decision && (
          <div className={styles.leaveDialog}>
            <p>
              <strong>{fullName(decision.request)}</strong> · {decision.request.leave_type_name} · {range(decision.request)}
              {' · '}{tn(t, decision.request.weekdays, '{count} weekday', '{count} weekdays')}
            </p>
            {decision.approve && (
              <p className={styles.muted}>{t('Approving creates the leave days (Monday to Friday) in the attendance.')}</p>
            )}
            {warnings(decision.request).map((n) => <p key={n} className={styles.dialogWarning}>{n}</p>)}
            <label className={styles.panelField}>
              <span>{decision.approve ? t('Note (optional)') : t('Reason (required)')}</span>
              <textarea className="textarea" rows="3" value={note} onChange={(e) => setNote(e.target.value)} data-autofocus />
            </label>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LeaveTab;
